import windowService from '../../../services/windowService';
import { UserResponse } from '../type/userResponse';
import licenseService from '../../../services/licenseService';
import eventBus from '../../../services/globalEventBus';
import userService from '../../../services/userService';
import inviteService from '../../../services/inviteService';
import { getRedirectURLLogin } from '../api/getRedirectURLLogin';
import WhiteLabel from '../../../filters/WhiteLabel';
import { User } from '../domain/User';
import { persistStoredUser } from '../utils/userStorage';
import { keepJwt, validateJwt } from '../../../services/jwt';

const removeHashFromUrl = (url: string): string => {
  if (url.includes('#/oauth2/callback')) {
    return url.replace('#/oauth2/callback', '/oauth2/callback');
  }
  return url;
};

export const useOAuth = () => {
  const userManager = window.getUserManager?.();

  const getUserIsAuthor = (userResponse: UserResponse) => {
    const siteShortCode = WhiteLabel.catalog?.shortCode || 'MG';
    return userResponse?.catalogs?.some(catalog => catalog.shortCode === siteShortCode);
  };

  const checkLicensesAndRedirect = (user: UserResponse) => {
    licenseService.getAllUnpairedLicenses().then(([groupLicenses]) => {
      if (groupLicenses.length > 0) {
        windowService.redirectTo('/pairing');
      } else {
        // Will redirect to fallback if there are invites
        inviteService.lookupInvitesForEmail(user.email).then((invitesResponse: { invites: any[] }) => {
          if (invitesResponse.invites.length > 0) {
            windowService.redirectTo('/pending-invites');
          } else {
            redirectFallback(user);
          }
        });
      }
    });
  };

  const redirectFallback = (user: UserResponse) => {
    let hash = userService.getRedirectHashAfterLogin(user);

    if (window.location.hash.includes('purchaseCheck')) {
      hash = '/dashboard?purchased=true';
    } else if (user.redirectURL && user.redirectURL.indexOf('onboarding') > -1) {
      hash = user.redirectURL.split('#')[1];
    } else if (user.redirectURL && user.redirectURL.indexOf('select-org-and-assign') > -1) {
      hash = user.redirectURL.split('#')[1];
    }
    windowService.redirectTo(hash);
  };

  const loginSuccess = (userResponse?: UserResponse) => {
    if (!userResponse) {
      return new Error('User response is undefined');
    }
    const directInviteOrgId = windowService.getParameterByName('directInviteOrgId', userResponse.redirectURL);
    const forEmail = windowService.getParameterByName('forEmail', userResponse.redirectURL);
    const prefix = windowService.getParameterByName('prefix', userResponse.redirectURL);
    const suffix = windowService.getParameterByName('suffix', userResponse.redirectURL);
    let route = windowService.getParameterByName('route', userResponse.redirectURL);

    if (window.location.hash.includes('purchaseCheck')) {
      checkLicensesAndRedirect(userResponse);
    } else if (directInviteOrgId !== undefined && forEmail !== undefined) {
      windowService.redirectTo('/invite/accept/' + directInviteOrgId + '/' + forEmail);
    } else if (prefix !== undefined && suffix !== undefined) {
      windowService.redirectTo('/redeem-invite/' + prefix + '/' + suffix);
    } else if (route !== undefined && !route.includes('login')) {
      if (route.includes('create')) {
        route = route + '/true';
      }
      if (!route.includes('purchaseCheck')) {
        // Remove route from storage so it's not there on subsequent logins
        sessionStorage.removeItem('redirectUrl');
        // userService.clearRedirectUrl();
        windowService.redirectTo(route.replace('%2F', '/'));
      } else {
        redirectFallback(userResponse);
      }
    } else if (userResponse.redirectURL && userResponse.redirectURL.includes('curriculum/onboarding')) {
      windowService.redirectTo(userResponse.redirectURL);
    } else if (userResponse.redirectURL && userResponse.redirectURL.startsWith('/onboarding/churchcares')) {
      sessionStorage.removeItem('redirectUrl');
      windowService.redirectTo(userResponse.redirectURL);
    } else {
      //Check for Unpaired Subscriptions, if so, take them to the license pairing flow, if not, redirect
      checkLicensesAndRedirect(userResponse);
    }
  };

  const setUserInLocalStorage = (userResponse: UserResponse): UserResponse => {
    const user = {
      ...userResponse,
      displayName: userResponse.displayName,
      firstName: userResponse.firstName,
      lastName: userResponse.lastName,
      logInEmail: userResponse.email,
      roles: userResponse.roles || [],
      impersonating: userResponse.impersonating,
      impersonatingWas: userResponse.impersonatingWas,
      redirectURL: sessionStorage.getItem('redirectUrl') || '',
      permissions: (userResponse.roles || []).flatMap(role => role.permissions.map(permission => permission.access)),
      userId: userResponse.userId,
      lastSelectedAccount: userResponse.lastSelectedAccount,
      isAuthor: getUserIsAuthor(userResponse),
    };

    persistStoredUser(user as unknown as User);
    eventBus.publish('UserLoggedIn', user);
    return user;
  };

  const setUserJWT = (token: string): Promise<void> => {
    if (validateJwt(token)) {
      keepJwt(token);
      return Promise.resolve();
    }
    return Promise.reject(new Error('Invalid JWT'));
  };

  const signInSSOUser = () => {
    if (!userManager) return Promise.reject(new Error('UserManager is not available'));
    eventBus.publish('SignInStarted');
    return userManager.signinRedirectCallback(removeHashFromUrl(window.location.href)).then(user => {
      return userService.authorizeV2(user.id_token, window.location.host).then(response => {
        const token = response.headers['set-authorization'];
        return setUserJWT(token)
          .then(() => {
            // Set the user in local storage
            const user = setUserInLocalStorage(response.data as UserResponse);
            // Synchronize if with the current session
            loginSuccess(user);
          })
          .catch(() => {
            // If not valid, let's just logout
            return Promise.reject(new Error('Something is wrong with the jwt'));
          });
      });
    });
  };

  const signInNonSSOUser = (code: string, state: string) => {
    const savedState = sessionStorage.getItem('state');
    if (savedState !== state) {
      // local storage state is not the same as the state in the query params
      // this means the user is trying to access the page directly, not through the SSO flow
      // so we need to reject the promise and redirect to the login page
      return Promise.reject(new Error('Invalid state parameter'));
    }
    eventBus.publish('SignInStarted');
    return userService.authorizeV1(code, window.location.host).then(response => {
      const token = response.headers['set-authorization'];
      return setUserJWT(token)
        .then(() => {
          // Set the user in local storage
          const user = setUserInLocalStorage(response.data as UserResponse);
          // Synchronize if with the current session
          loginSuccess(user);
        })
        .catch(() => {
          // If not valid, let's just logout
          return Promise.reject(new Error('Something is wrong with the jwt'));
        });
    });
  };

  const signInSSORedirect = () => {
    if (!userManager) return Promise.reject(new Error('UserManager is not available'));
    return userManager.signinRedirect();
  };

  const signInNonSSORedirect = () => {
    return getRedirectURLLogin(window.location.href).then(result => {
      windowService.redirectTo(result);
    });
  };

  return {
    loginSuccess,
    signInSSOUser,
    signInNonSSOUser,
    signInSSORedirect,
    signInNonSSORedirect,
  };
};
